import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Loader2, Pause, TrendingUp, TrendingDown } from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { CHART_COLORS, ROTATED_LABEL_CHART_MARGIN } from '@/lib/theme'
import type { ChartType } from './types'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'

function DeltaIndicator({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) {
    return <span className="text-xs text-muted-foreground">-</span>
  }
  const delta = previous !== 0 ? ((current - previous) / previous) * 100 : current > 0 ? 100 : 0
  const isPositive = delta > 0
  const isZero = delta === 0

  if (isZero) {
    return <span className="text-xs text-muted-foreground">0%</span>
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        isPositive ? 'text-[#2E7D5B]' : 'text-[#941052]'
      )}
      title={`${isPositive ? '+' : ''}${delta.toFixed(1)}% vs previous period`}
    >
      {isPositive ? (
        <TrendingUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <TrendingDown className="h-3 w-3" aria-hidden="true" />
      )}
      {Math.abs(delta).toFixed(1)}%
    </span>
  )
}

interface ReportPreviewProps {
  chartType: ChartType
  reportResults: any
  comparisonResults: any
  isExecuting: boolean
  isLivePreviewEnabled: boolean
  onLivePreviewChange: (enabled: boolean) => void
  // When false, hides the "Live"/"Paused" toggle and relabels the header — used
  // for the read-only View modal where there is no live-preview concept.
  showLiveToggle?: boolean
  title?: string
}

export function ReportPreview({
  chartType,
  reportResults,
  comparisonResults,
  isExecuting,
  isLivePreviewEnabled,
  onLivePreviewChange,
  showLiveToggle = true,
  title = 'Live Preview',
}: ReportPreviewProps) {
  return (
    <div className="lg:sticky lg:top-4 lg:self-start">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>
                {reportResults
                  ? `Showing ${reportResults.data?.length || 0} results`
                  : 'Configure fields to see preview'}
              </CardDescription>
            </div>
            {showLiveToggle && (
              <div className="flex items-center gap-2">
                {!isLivePreviewEnabled && (
                  <Badge variant="outline" className="gap-1">
                    <Pause className="h-3 w-3" aria-hidden="true" />
                    Paused
                  </Badge>
                )}
                <div className="flex items-center gap-2">
                  <Label htmlFor="live-preview" className="text-sm text-muted-foreground">
                    Live
                  </Label>
                  <Switch
                    id="live-preview"
                    checked={isLivePreviewEnabled}
                    onCheckedChange={onLivePreviewChange}
                    aria-label="Enable live preview"
                  />
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isExecuting ? (
            <div className="flex items-center justify-center py-12">
              <Loader2
                className="h-8 w-8 animate-spin text-muted-foreground"
                aria-label="Loading report results"
              />
            </div>
          ) : !reportResults ? (
            <div className="p-8 text-center border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">
                {isLivePreviewEnabled
                  ? 'Select a data source and fields to see live preview'
                  : 'Enable live preview or click "Run Report"'}
              </p>
            </div>
          ) : reportResults.data?.length === 0 ? (
            <div className="p-8 text-center border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">No results found matching your criteria</p>
            </div>
          ) : (
            <>
              {/* Comparison Summary */}
              {comparisonResults && comparisonResults.data && (
                <ComparisonSummary
                  reportResults={reportResults}
                  comparisonResults={comparisonResults}
                />
              )}

              {/* Table View */}
              {chartType === 'table' && <TableView data={reportResults.data} />}

              {/* Bar Chart */}
              {chartType === 'bar' && <ChartView type="bar" data={reportResults.data} />}

              {/* Line Chart */}
              {chartType === 'line' && <ChartView type="line" data={reportResults.data} />}

              {/* Area Chart */}
              {chartType === 'area' && <ChartView type="area" data={reportResults.data} />}

              {/* Pie Chart */}
              {chartType === 'pie' && <PieView data={reportResults.data} />}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ComparisonSummary({
  reportResults,
  comparisonResults,
}: {
  reportResults: any
  comparisonResults: any
}) {
  const firstRow = reportResults.data[0]
  if (!firstRow) return null
  const numericKeys = Object.keys(firstRow).filter((k) => typeof firstRow[k] === 'number')
  const key = numericKeys[0]

  return (
    <div className="mb-4 p-4 bg-muted/50 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-sm font-medium">Period Comparison</span>
        </div>
        <Badge variant="outline" className="text-xs">
          {comparisonResults.period}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">Current Period</p>
          <p className="text-lg font-semibold">{reportResults.data?.length || 0} rows</p>
          {key && (
            <p className="text-sm text-muted-foreground">
              Sum:{' '}
              {reportResults.data
                .reduce((sum: number, row: any) => sum + (parseFloat(row[key]) || 0), 0)
                .toLocaleString()}
            </p>
          )}
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">{comparisonResults.period}</p>
          <p className="text-lg font-semibold">{comparisonResults.data?.length || 0} rows</p>
          {key &&
            comparisonResults.data.length > 0 &&
            (() => {
              const currentTotal = reportResults.data.reduce(
                (sum: number, row: any) => sum + (parseFloat(row[key]) || 0),
                0
              )
              const compTotal = comparisonResults.data.reduce(
                (sum: number, row: any) => sum + (parseFloat(row[key]) || 0),
                0
              )
              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Sum: {compTotal.toLocaleString()}
                  </span>
                  <DeltaIndicator current={currentTotal} previous={compTotal} />
                </div>
              )
            })()}
        </Card>
      </div>
    </div>
  )
}

// Matches an ISO date or date-time so date columns render as "Jun 15, 2025"
// instead of a raw "2025-06-15T00:00:00.000Z". Numbers are matched first by
// typeof, so this only ever applies to date-like strings.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/

function renderCell(value: any): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number')
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) return formatDate(value)
  return String(value)
}

function TableView({ data }: { data: any[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" role="table" aria-label="Report results">
        <thead>
          <tr className="border-b">
            {Object.keys(data[0] || {}).map((key) => (
              <th key={key} className="text-left p-2 font-medium">
                {key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row: any, index: number) => (
            <tr key={index} className="border-b hover:bg-muted/50">
              {Object.values(row).map((value: any, cellIndex: number) => (
                <td key={cellIndex} className="p-2">
                  {renderCell(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ChartView({ type, data }: { type: 'bar' | 'line' | 'area'; data: any[] }) {
  const keys = Object.keys(data[0] || {})
  const numericKeys = keys.slice(1).filter((key) => typeof data[0][key] === 'number')

  const ChartComponent = type === 'bar' ? BarChart : type === 'area' ? AreaChart : LineChart

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ChartComponent data={data} margin={ROTATED_LABEL_CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={keys[0]}
          angle={-45}
          textAnchor="end"
          height={100}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v))}
        />
        <Tooltip
          content={<ChartTooltipContent />}
          formatter={(value: number) =>
            value.toLocaleString(undefined, { maximumFractionDigits: 2 })
          }
        />
        <Legend />
        {numericKeys.map((key, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length]
          if (type === 'bar') {
            return <Bar key={key} dataKey={key} fill={color} name={key} />
          }
          if (type === 'area') {
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                fill={color}
                fillOpacity={0.3}
                strokeWidth={2}
                name={key}
              />
            )
          }
          return (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={2}
              name={key}
            />
          )
        })}
      </ChartComponent>
    </ResponsiveContainer>
  )
}

function PieView({ data }: { data: any[] }) {
  const firstRow = data[0] || {}
  const keys = Object.keys(firstRow)
  const aggregationPattern = /\((Count|Sum|Avg|Min|Max)\)$/i
  const numericKey =
    keys.find((key) => aggregationPattern.test(key)) ||
    keys.find((key) => {
      const val = firstRow[key]
      return typeof val === 'number' || (val !== null && !isNaN(Number(val)))
    }) ||
    ''
  const labelKey = keys.find((key) => key !== numericKey) || keys[0] || ''

  if (!numericKey || !labelKey) {
    return (
      <p className="text-muted-foreground text-center py-8">Unable to determine chart data keys</p>
    )
  }

  const pieData = data
    .filter((row: Record<string, unknown>) => row[labelKey] !== null && row[labelKey] !== undefined)
    .map((row: Record<string, unknown>) => ({
      ...row,
      [numericKey]: Number(row[numericKey]) || 0,
      [labelKey]: String(row[labelKey] || 'Unknown'),
    }))

  if (pieData.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No data available for pie chart</p>
  }

  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={pieData.length <= 6}
            label={
              pieData.length <= 6
                ? ({ percent }: { percent: number }) => `${(percent * 100).toFixed(0)}%`
                : false
            }
            outerRadius={120}
            innerRadius={pieData.length > 10 ? 40 : 0}
            fill={CHART_COLORS[0]}
            dataKey={numericKey}
            nameKey={labelKey}
          >
            {pieData.map((_entry: unknown, index: number) => (
              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            content={<ChartTooltipContent />}
            formatter={(value: number) =>
              value.toLocaleString(undefined, { maximumFractionDigits: 2 })
            }
          />
          <Legend />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  )
}
